import * as THREE from 'three';

export class FikoMeshOptimizer {
    private wasmOptimizer: any;

    constructor(wasmModule?: any) {
        if (wasmModule?.MeshOptimizer) {
            try { this.wasmOptimizer = new wasmModule.MeshOptimizer(); }
            catch (e) { this.wasmOptimizer = null; }
        }
    }

    optimizeGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
        if (!geometry.index || !geometry.attributes.position) return geometry;
        const positions = geometry.attributes.position.array as Float32Array;
        const indices = geometry.index.array;

        try {
            if (this.wasmOptimizer?.optimize_vertex_cache) {
                const optimized = this.wasmOptimizer.optimize_vertex_cache();
                if (optimized) {
                    geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(optimized), 1));
                    return geometry;
                }
            }

            // JS fallback: remove degenerate + simple reorder
            const clean = this._removeDegenerates(positions, indices);
            geometry.setIndex(new THREE.BufferAttribute(this._reorderTriangles(clean), 1));
        } catch (e) { console.error('FikoMeshOptimizer: failed', e); }
        return geometry;
    }

    simplify(geometry: THREE.BufferGeometry, targetRatio: number): THREE.BufferGeometry {
        if (!geometry.index) return geometry.clone();
        targetRatio = Math.max(0.01, Math.min(1.0, targetRatio));

        if (this.wasmOptimizer?.simplify_qem) {
            try {
                const positions = geometry.attributes.position.array as Float32Array;
                this.wasmOptimizer.set_mesh(Array.from(positions), Array.from(new Uint32Array(geometry.index.array)));
                const result = this.wasmOptimizer.simplify_qem(targetRatio);
                if (result && result.length > 0) {
                    const simplified = geometry.clone();
                    simplified.setIndex(new THREE.BufferAttribute(new Uint32Array(result), 1));
                    simplified.computeVertexNormals();
                    return simplified;
                }
            } catch (e) {}
        }

        // JS fallback: uniform sampling
        const indices = geometry.index.array;
        const triCount = indices.length / 3;
        const target = Math.max(1, Math.floor(triCount * targetRatio));
        const step = triCount / target;
        const newIdx: number[] = [];
        for (let i = 0; i < target; i++) {
            const ti = Math.floor(i * step) * 3;
            if (ti + 2 < indices.length) newIdx.push(indices[ti], indices[ti+1], indices[ti+2]);
        }
        const simplified = new THREE.BufferGeometry();
        simplified.setAttribute('position', geometry.attributes.position.clone());
        simplified.setIndex(newIdx);
        simplified.computeVertexNormals();
        return simplified;
    }

    generateLODChain(geometry: THREE.BufferGeometry, levels: number = 3): Array<{geometry: THREE.BufferGeometry; ratio: number}> {
        const chain = [{ geometry: geometry.clone(), ratio: 1.0 }];
        for (let i = 1; i < levels; i++) {
            const ratio = Math.pow(0.5, i);
            chain.push({ geometry: this.simplify(geometry, ratio), ratio });
        }
        return chain;
    }

    hasWasmOptimizer(): boolean { return !!this.wasmOptimizer; }

    private _removeDegenerates(positions: Float32Array, indices: ArrayLike<number>): number[] {
        const clean: number[] = [];
        for (let i = 0; i < indices.length; i += 3) {
            const a = indices[i], b = indices[i+1], c = indices[i+2];
            if (a === b || b === c || c === a) continue;
            clean.push(a, b, c);
        }
        return clean;
    }

    private _reorderTriangles(indices: number[]): Uint32Array {
        const tris: Array<{idx: number[]; score: number}> = [];
        for (let i = 0; i < indices.length; i += 3) {
            tris.push({ idx: [indices[i], indices[i+1], indices[i+2]], score: (indices[i] + indices[i+1] + indices[i+2]) / 3 });
        }
        tris.sort((a, b) => a.score - b.score);
        const result = new Uint32Array(indices.length);
        for (let i = 0; i < tris.length; i++) {
            result[i*3] = tris[i].idx[0];
            result[i*3+1] = tris[i].idx[1];
            result[i*3+2] = tris[i].idx[2];
        }
        return result;
    }
}
